import { useEffect, useState } from 'react';
import api from '../api/axiosConfig';

// Subscription prices are banded by the visitor's country (purchasing power
// parity), so they cannot be hardcoded in the UI. The backend resolves the
// band from the request IP and is the only thing that decides what someone
// pays -- this hook just displays the answer.

export interface Pricing {
    band: string;
    currency: string;
    monthly: number;
    yearly: number;
}

// Band A, matching core/pricing_bands.py. Shown only until the request
// lands, so the page never renders an empty price.
export const FALLBACK_PRICING: Pricing = {
    band: 'A',
    currency: 'USD',
    monthly: 11.99,
    yearly: 109.99,
};

// Where each currency is spent, so Intl formats it the way it is written
// there: ₡3.800 rather than CRC 3,800, $15.900 rather than COP 15,900.
const CURRENCY_LOCALE: Record<string, string> = {
    MXN: 'es-MX',
    CRC: 'es-CR',
    COP: 'es-CO',
    PEN: 'es-PE',
    GTQ: 'es-GT',
    HNL: 'es-HN',
    NIO: 'es-NI',
};

/**
 * A price with its currency, written as that currency is written where it is
 * spent. Falls back to a plain amount if the browser cannot format it.
 */
export const formatMoney = (
    amount: number, currency = 'USD', language = 'en'
): string => {
    const locale = CURRENCY_LOCALE[currency]
        || { es: 'es-419', pt: 'pt-BR' }[language] || 'en-US';
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            // Cents on $6.49, none on $109 or ₡2.900 — the way each is priced.
            minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
            maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        }).format(amount);
    } catch {
        return `${currency === 'USD' ? '$' : `${currency} `}${formatPrice(amount, language)}`;
    }
};

/**
 * Format a price for display, trimming the cents on whole amounts.
 *
 * Portuguese writes decimals with a comma. The Spanish copy on the pricing
 * page has always used a period, so only pt is switched.
 */
export const formatPrice = (amount: number, language = 'en'): string => {
    const text = Number.isInteger(amount) ? `${amount}` : amount.toFixed(2);
    return language === 'pt' ? text.replace('.', ',') : text;
};

/** Monthly-equivalent cost of the yearly plan, for "$9/mo billed yearly". */
export const monthlyEquivalent = (pricing: Pricing, language = 'en'): string =>
    formatPrice(Math.round((pricing.yearly / 12) * 100) / 100, language);

/** The same, with its currency symbol. */
export const monthlyEquivalentMoney = (pricing: Pricing, language = 'en'): string =>
    formatMoney(Math.round((pricing.yearly / 12) * 100) / 100, pricing.currency, language);

/** Percentage saved by paying yearly instead of monthly. */
export const yearlySavingPercent = (pricing: Pricing): number =>
    Math.round((1 - pricing.yearly / (pricing.monthly * 12)) * 100);

/**
 * The visitor's banded prices. Works logged out -- the pricing endpoint is
 * public, because someone deciding whether to sign up needs to see the cost.
 */
export const usePricing = (): { pricing: Pricing; loaded: boolean } => {
    const [pricing, setPricing] = useState<Pricing>(FALLBACK_PRICING);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get('/billing/pricing/')
            .then(({ data }) => {
                if (!cancelled && data?.pricing) setPricing(data.pricing);
            })
            .catch(() => {
                // Keep the fallback: showing full price is the safe failure.
            })
            .finally(() => {
                if (!cancelled) setLoaded(true);
            });
        return () => { cancelled = true; };
    }, []);

    return { pricing, loaded };
};
